/**
 * Detect broken tables (space/tab separated with --- separator rows)
 * and convert them to proper markdown tables with pipes.
 */
function fixBrokenTables(text) {
  const lines = text.split('\n');
  const result = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const isSeparator = /^[\s:|-]*$/.test(line) && (line.match(/-{3,}/g) || []).length >= 2 && !line.includes('|');

    if (isSeparator && i > 0) {
      const sepParts = line.trim().split(/\s+/).filter(p => /-{2,}/.test(p));
      const numCols = sepParts.length;

      if (numCols >= 2) {
        let headerStart = i - 1;
        const headerLines = [];
        for (let h = i - 1; h >= 0 && h >= i - 3; h--) {
          const hl = lines[h].trim();
          if (!hl || hl.startsWith('#') || hl.startsWith('-') || hl.startsWith('*') || hl.startsWith('>')) break;
          if (hl.startsWith('|') && hl.endsWith('|')) break;
          headerLines.unshift(hl);
          headerStart = h;
        }

        const headerText = headerLines.join(' ');
        const headers = headerText.split(/\s{2,}|\t/).map(h => h.trim()).filter(Boolean);

        if (headers.length >= 2) {
          const dataRows = [];
          let j = i + 1;
          while (j < lines.length) {
            const dl = lines[j].trim();
            if (!dl || dl.startsWith('#') || dl.startsWith('---') || dl.startsWith('*') || dl.startsWith('>')) break;
            if (dl.startsWith('|') && dl.endsWith('|')) break;
            const cells = dl.split(/\s{2,}|\t/).map(c => c.trim()).filter(Boolean);
            if (cells.length >= 2) {
              dataRows.push(cells);
              j++;
            } else {
              break;
            }
          }

          if (dataRows.length > 0) {
            while (result.length > 0) {
              let toRemove = 0;
              for (let r = result.length - 1; r >= 0 && toRemove < headerLines.length; r--) {
                if (headerLines.includes(result[r].trim()) || result[r].trim() === headerLines[toRemove]?.trim()) {
                  toRemove++;
                }
              }
              for (let r = 0; r < headerLines.length && result.length > 0; r++) {
                result.pop();
              }
              break;
            }

            const actualCols = Math.max(headers.length, ...dataRows.map(r => r.length));
            while (headers.length < actualCols) headers.push('');

            result.push('');
            result.push('| ' + headers.join(' | ') + ' |');
            result.push('|' + headers.map(() => '---').join('|') + '|');
            for (const row of dataRows) {
              while (row.length < actualCols) row.push('');
              result.push('| ' + row.join(' | ') + ' |');
            }
            result.push('');

            i = j;
            continue;
          }
        }
      }
    }

    result.push(line);
    i++;
  }

  return result.join('\n');
}

/**
 * Normalize LLM markdown so ReactMarkdown can parse tables correctly.
 */
export function normalizeMarkdown(text) {
  if (!text) return '';

  let fixed = text;

  // Step 1: Ensure blank line before headers
  fixed = fixed.replace(/([^\n])\n(#{1,3} )/g, '$1\n\n$2');

  // Step 2: Ensure blank line before markdown tables (line starting with |)
  fixed = fixed.replace(/([^\n|])\n(\|[^|])/g, '$1\n\n$2');

  // Step 3: Fix separator rows missing leading pipe: "---|---" → "|---|---|"
  fixed = fixed.replace(/^(\s*)(-{3,}\|(?:-{3,}\|?)*-{3,})\s*$/gm, (_, indent, sep) => {
    const cols = sep.split('|').filter(s => s.trim());
    return indent + '|' + cols.map(() => '---').join('|') + '|';
  });

  // Step 4: Convert broken tables to proper markdown
  fixed = fixBrokenTables(fixed);

  // Step 5: Clean up excessive blank lines
  fixed = fixed.replace(/\n{3,}/g, '\n\n');

  return fixed;
}
